import { Identifier } from 'react-admin'
import type { CustomI18NITF } from '@activate-inc/activate-ui/types'
import { PanelChoice } from '@/types/panel'
import { RaRecord } from 'ra-core'

export type ThemeName = 'light' | 'dark'

export interface UserPersonalITF {
  DisplayName: CustomI18NITF
  FullName: CustomI18NITF
  SortName: CustomI18NITF
}

export interface UserITF {
  p8Id: string
  acId: string
  UserToken: string
  UserName: string
  NickName: CustomI18NITF
  CreatedDate: number
  IsVisitor: boolean
  ClientKey: string
  LandingSource: string
  AcquireChannel: string
  IsAcAdmin?: boolean
  PortfolioTeam?: boolean
  UserPersonal: UserPersonalITF
  UserContext: {
    CurrentGroupId: string
    CurrentGroupName?: CustomI18NITF
    CurrentGroupLogoFilename?: string
    CurrentBuildingId?: string
    MemberRoleInGroup: string
    PermissionsHash: Record<string, boolean>
    /** Effective feature flags (global ∧ group). A missing key means disabled. */
    EnabledFeatureFlags?: Record<string, boolean>
    /** Per-feature building scope (entity acIds). No entry = every building. */
    FeatureBuildings?: Record<string, string[]>
    AccessBuildingIds: string[]
    PermissionVersion?: number
  }
  Preference?: {
    DefaultGroupId?: string
    DefaultBuildingId?: string
  }
  /**
   * Every group the user holds a membership in (the login / CheckIn payload
   * carries the token's list). A portfolio link (/p/:gId) is only followed into
   * one of these for a non-AcAdmin user; see hooks/usePortfolioUrlScope.ts.
   */
  Memberships?: MembershipITF[]
}

export interface MembershipITF {
  GroupId: string
  GroupName?: CustomI18NITF
  MemberIdInGroup?: string
  MemberRoleInGroup?: string
  /** Member.Status: Tentative / Registered / Pending / Verified, or Archived / Offboarded for a dead one. */
  MembershipStatus?: string
}

export type RoleStatus = 'Active' | 'Archived'

export interface RoleITF {
  acId?: string
  Name: string
  DisplayText: string
  Description?: string
  Weight: number
  MemberPermissions: string[]
  Status: RoleStatus
  IsCustom: boolean
}

export interface FeatureFlagITF {
  FeatureName: string
  GlobalEnabled: boolean
  GroupEnabled?: boolean
  Description?: string
}

export interface SystemSettingITF {
  SettingKey: string
  DisplayName: string
  Description?: string
  ValueType: 'Phone' | 'Email' | 'Text'
  AllowedValues?: string[] | null
  Value: string
  DefaultValue: string
  ModifiedBy?: string | null
  ModifiedDate?: number | null
}

export interface PermissionByFeatureEntryITF {
  Name: string
  DisplayName?: string
  Description?: string
  Route?: string
  // Member statuses the permission activates for, e.g. { Registered: true, Verified: true }.
  // Returned by newer GetPermissionsByFeature backends; no UI consumes it yet.
  AvailableToMemberStatus?: Record<string, boolean>
  // AcAdmin-only (Global*) permissions: shown locked in the roles editor; the
  // backend rejects any grant/revoke of them (role.acadmin.permission.locked).
  AcAdminOnly?: boolean
}

export interface PermissionsByFeatureITF {
  [feature: string]: PermissionByFeatureEntryITF[]
}

export type QRCodConfigContextValue = {
  getOptionValue: (name: string) => any
  setOptionValue: (name: string, value: string | boolean | number | QRCodeType) => void
}

export type SetOnSave = (onSave?: (values: object, redirect: any) => void) => void

export interface Category extends RaRecord {
  name: string
}

export interface Product extends RaRecord {
  category_id: Identifier
  description: string
  height: number
  image: string
  price: number
  reference: string
  stock: number
  thumbnail: string
  width: number
}

export interface Customer extends RaRecord {
  first_name: string
  last_name: string
  address: string
  stateAbbr: string
  city: string
  zipcode: string
  avatar: string
  birthday: string
  first_seen: string
  last_seen: string
  has_ordered: boolean
  latest_purchase: string
  has_newsletter: boolean
  groups: string[]
  nb_commands: number
  total_spent: number
}

export type OrderStatus = 'ordered' | 'delivered' | 'cancelled'

export interface Order extends RaRecord {
  status: OrderStatus
  basket: BasketItem[]
  date: Date
  total: number
}

export interface BasketItem {
  product_id: Identifier
  quantity: number
}

export interface Invoice extends RaRecord {}

export type ReviewStatus = 'accepted' | 'pending' | 'rejected'

export interface Review extends RaRecord {
  date: string
  status: ReviewStatus
  customer_id: Identifier
  product_id: Identifier
}

export enum QRCodeType {
  Account = 'Account',
  Property = 'Property',
  Activate = 'Activate',
  Custom = 'Custom',
}
export enum QRStatusType {
  Draft = 'Draft',
  Active = 'Active',
  PendingApproval = 'PendingApproval',
}

export enum QRCodeFunctionType {
  Announcements = 'Announcements',
  Collections = 'Collections',
  Communication = 'Communication',
  EmbeddedStaticUrl = 'EmbeddedStaticUrl',
  Emergency = 'Emergency',
  Feedback = 'Feedback',
  CustomerFeedback = 'CustomerFeedback',
  FieldEvent = 'FieldEvent',
  FieldMarketing = 'FieldMarketing',
  FrontDesk = 'FrontDesk',
  GoogleMap = 'GoogleMap',
  Html = 'Html',
  JoinWifi = 'JoinWifi',
  Maintenance = 'Maintenance',
  MoveIn = 'MoveIn',
  MoveOut = 'MoveOut',
  PDF = 'PDF',
  EmbeddedFile = 'EmbeddedFile',
  Portfolio = 'Portfolio',
  Purchase = 'Purchase',
  QrHub = 'QrHub',
  Renewal = 'Renewal',
  SimpleForm = 'SimpleForm',
  SocialMedia = 'SocialMedia',
  Static = 'StaticUrl',
  SupplierCheckin = 'SupplierCheckin',
  Support = 'Support',
  Vacancy = 'Vacancy',
  Video = 'Video',
  PhoneCall = 'PhoneCall',
  DownloadApp = 'DownloadApp',
  FireExtinguisher = 'FireExtinguisher',
  AEDInspection = 'AEDInspection',
  BathroomService = 'BathroomService',
  LeadCapture = 'LeadCapture',
  EVRequest = 'EVRequest',
  CheckIn = 'CheckIn',
  RoomRental = 'RoomRental',
  OpenAI = 'OpenAI',
  NewSupplier = 'NewSupplier',
  EquipmentInspection = 'EquipmentInspection',
  AngusServiceRequest = 'AngusServiceRequest',
  Inspection = 'Inspection',
  InspectionCheckIn = 'InspectionCheckIn',
  RoundInspection = 'RoundInspection',
  AngusOnDemandWorkOrder = 'AngusOnDemandWorkOrder',
  CommentCard = 'CommentCard',
  GuestCheckin = 'GuestCheckin',
  GuestCheckOut = 'GuestCheckOut',
  ResidentCheckIn = 'ResidentCheckIn',
  StudentCheckin = 'StudentCheckin',
  ServiceRequests = 'ServiceRequests',
  MechanicalSpace = 'MechanicalSpace',
  Inventory = 'Inventory',
  ActivitySchedule = 'ActivitySchedule',
  EventRegistration = 'EventRegistration',
  RoundReport = 'RoundReport',
  TenantHealth = 'TenantHealth',
  TenantPerformance = 'TenantPerformance',
  SupplierCheckReport = 'SupplierCheckReport',
  GuestLogReport = 'GuestLogReport',
  FeedbackLocationReport = 'FeedbackLocationReport',
  LadderSafety = 'LadderSafety',
  BurnPermit = 'BurnPermit',
  Permit = 'Permit',
  BEServiceRequest = 'BEServiceRequest',
  MoveOutResident = 'MoveOutResident',
  MaintenanceRequest = 'MaintenanceRequest',
  RoofAccess = 'RoofAccess',
  TenantHVAC = 'TenantHVAC',
  EquipmentLookup = 'EquipmentLookup',
  EquipmentTrend = 'EquipmentTrend',
  CleaningRoundReport = 'CleaningRoundReport',
  SupplierList = 'SupplierList',
  ResidentCheckOut = 'ResidentCheckOut',
  BathroomServiceReport = 'BathroomServiceReport',
  // Retired (#3448) in favour of InspectionOverview; kept for hub items not yet migrated.
  PortfolioInspectionDashboard = 'PortfolioInspectionDashboard',
  BuildingInspectionDashboard = 'BuildingInspectionDashboard',
  InspectionOverview = 'InspectionOverview',
  StaffCheckIn = 'StaffCheckIn',
  TourCheckin = 'TourCheckin',
  RoofAccessCarousel = 'RoofAccessCarousel',
  SecurityPatrolDashboard = 'SecurityPatrolDashboard',
  PropertyCHARReport = 'PropertyCHARReport',
  JobReport = 'JobReport',
  MobileEmployee = 'MobileEmployee',
  ShowQR = 'ShowQR',
  FeedbackList = 'FeedbackList',
  VideoGuide = 'VideoGuide',
  ResidentIndex = 'ResidentIndex',
  CustomFeedback = 'CustomFeedback',
  ParkingLocation = 'ParkingLocation',
  UpcomingEvent = 'UpcomingEvent',
  Lookbook = 'Lookbook',
  IncidentReport = 'IncidentReport',
  ComprehensiveFeedbackReport = 'ComprehensiveFeedbackReport',
  MobileMap = 'MobileMap',
  MappingHub = 'MappingHub',
  PcaHub = 'PcaHub',
  VisitorCheckin = 'VisitorCheckin',
  VisitorRegistration = 'VisitorRegistration',
  VisitorLog = 'VisitorLog',
  LeaseAbstractionReport = 'LeaseAbstractionReport',
  PortfolioLeaseAbstraction = 'PortfolioLeaseAbstraction',
  CapitalPlanningReport = 'CapitalPlanningReport',
  PortfolioCapitalPlanning = 'PortfolioCapitalPlanning',
  PMDashboardReport = 'PMDashboardReport',
  PortfolioPMDashboard = 'PortfolioPMDashboard',
  ComplianceDashboardReport = 'ComplianceDashboardReport',
  PortfolioComplianceDashboard = 'PortfolioComplianceDashboard',
  BidBoard = 'BidBoard',
}

export enum QRCodeConfigEnum {
  QRType = 'qrtype',
  BGColor = 'bgColor',
  EyeColor = 'eyeColor',
  ECLevel = 'eclevel',
  FGColor = 'fgColor',
  FillColor = 'fillColor',
  Size = 'size',
  StripeText = 'stripeText',
  LogoImage = 'logoImage',
  hasLogo = 'hasLogo',
  EyeFrameShape = 'eye',
  EyeBallShape = 'eyeball',
  BodyShape = 'body',
  BackgroundDimming = 'backgroundDimming',
  FileType = 'fileType',
  QrStyle = 'QrStyle',
  DotsType = 'dotsType',
  CornersSquareType = 'cornersSquareType',
  CornersDotType = 'cornersDotType',
  ErrorCorrectionLevel = 'errorCorrectionLevel',
  ImageMargin = 'imageMargin',
  HideBackgroundDots = 'hideBackgroundDots',
  BackgroundImage = 'backgroundImage',
  Margin = 'margin',
  LogoSize = 'imageSize',
  GradientColor = 'gradientColor',
  GradientType = 'gradientType',
  GradientRotation = 'gradientRotation',
}

export interface QrConfigV2 {
  dotsType?: string
  dotsColor?: string
  bgColor?: string
  cornersSquareType?: string
  cornersSquareColor?: string
  cornersDotType?: string
  cornersDotColor?: string
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H'
  image?: string
  imageMargin?: number
  hideBackgroundDots?: boolean
  backgroundImage?: string
  size?: number
  qrConfigVersion?: 2
  /* kept for back-compat with v1 saved configs */
  body?: string
  bodyColor?: string
  eye?: string
  eyeBall?: string
  eye1Color?: string
  eye2Color?: string
  eye3Color?: string
  eyeBall1Color?: string
  eyeBall2Color?: string
  eyeBall3Color?: string
  correctLevel?: string | number
  outFileType?: string
  logo?: string
}

export type LangType = {
  en?: string
  zh?: string
} & {
  [key: string]: string
}

export interface IBuilding {
  BuildingId: string
  BuildingName: LangType
  Score: number
}

declare global {
  interface Window {
    restServer: any
    PDF_PAGE_DONE: boolean
  }

  interface IOption {
    label: string
    value: string | number
    child?: IOption[]
    [key: string]: any
  }
  interface ISelectOption {
    [key: string]: any
  }
}

declare module '@activate-inc/activate-ui'

export interface IEntity {
  id: string
  BuildingId: string
  BuildingType: string
  Code: string
  GroupId: string
  Name: LangType
  Status: string
  Type: string
  acId: string
  LogoFilename?: string
  PropertyTag?: Record<string, any>
  ManagementTag?: { TagId?: string; Name?: string }
}

export interface IEntityType {
  EntityType: string
  Name: string
  Score: number | 'NA'
}

export interface IBuildingSummary {
  BuildingId: string
  BuildingName: LangType
  EntityTypes: IEntityType[]
  Score: number
}

export interface IEntitiesReport {
  AverageScore: number
  Name: string
  ScoredNumber: number
  TotalScore: string
  EntityType?: string
  First: { Trend: number }
  Second: { Trend: number }
  Third: { Trend: number }
  Fourth: { Trend: number }
}

export interface ITopic {
  NegativeScoreCount: number
  PositiveScoreCount: number
  TotalScoreCount: number
  Topic: string
  Count?: number
}

export interface ResponseTopics {
  Topics: ITopic[]
}

export interface IComment {
  EntityId: string
  EntityType: string
  FeedbackId: string
  Score: number
  Text: string
  SubmitDate: number
  acId: string
  CreatedDate: number
}

export interface ResponseComment {
  Comments: IComment[]
}

export interface IWordCloud {
  Label: string
  Value: number
  SentimentScore: number
}

export interface IStarBucket {
  Score: number
  Count: number
}

export interface IEntityTypeAnalytic {
  BuildingId: string
  AverageScore: number
  StarBuckets: IStarBucket[]
}

export type PictureUploadedInfo = {
  location: string
}

export interface PictureManagerInterface {
  readonly region?: string
  readonly accessKeyId?: string
  readonly secretAccessKey?: string
  uploadImage: (username: string, file: File) => Promise<PictureUploadedInfo>
  deleteImage: (username: string, pictureUrl: string) => Promise<void>
  deleteUserFolder: (username: string) => Promise<void>
}

export interface IHubQrCode {
  id: string
  QrId: string
  HubId: string
  Name: string
  ImageUrl: string
  PointingUrl: string
  BannerUrl: string
}

export enum QRCodeFrame {
  NONE = 'none',
  BOX_BOTTOM = 'box-bottom',
  BOX_TOP = 'box-top',
  BANNER_TOP = 'banner-top',
  BANNER_BOTTOM = 'banner-bottom',
  BALLOON_BOTTOM = 'balloon-bottom',
  BALLOON_TOP = 'balloon-top',
  CIRCULAR = 'circular',
  TEXT_ONLY = 'text-only',
  FOCUS = 'focus',
  // BOX_LIGHT = 'box-light',
}

export interface ChoiceITF extends PanelChoice {
  Label: string
  Text: CustomI18NITF
  Value: number
}

export const HubItemStage = [
  { label: 'QR Engineering', value: 'EngineerReview' },
  { label: 'CS Review', value: 'CSReview' },
  { label: 'Client Review', value: 'ClientReview' },
  { label: 'Approved', value: 'Approved' },
]

// ========== Mapping Hub (specs#3281) ==========

export type MappingHubStatus = 'Active' | 'Archived'
export type MapCategoryType = 'Equipment' | 'Location'
export type MapScanStatus = 'None' | 'Pending' | 'Parsed'

export interface MapCategoryITF {
  acId: string
  GroupId: string
  /** Absent on group-level defaults; set on building-local additions */
  BuildingId?: string | null
  Name: string
  /** Fixed at creation — never editable */
  Type: MapCategoryType
  Icon?: string
  Color?: string
  TileImageUrl?: string
  TagPrefix?: string
  SortOrder?: number
  Status: MappingHubStatus
  /** Present on GetMappingHub payload rows */
  ItemCount?: number
}

export interface MapCategorySettingITF {
  acId: string
  BuildingId: string
  CategoryId: string
  Hidden?: boolean
  SortOrder?: number
}

// One row per component rating line as printed on the plate (multi-part
// plates: unit MCA/MOCP, compressors, fan motors). AmpsType is the printed
// amps label (FLA/RLA/LRA/MCA/MOCP).
export interface MapRatingITF {
  Component?: string | null
  Qty?: string | null
  Volts?: string | null
  Phase?: string | null
  Hz?: string | null
  Amps?: string | null
  AmpsType?: string | null
  HP?: string | null
  Note?: string | null
}

export interface MapItemITF {
  acId: string
  GroupId: string
  BuildingId: string
  CategoryId: string
  Type: MapCategoryType
  Tag: string
  Name?: string
  SubLocation?: string
  Note?: string
  Make?: string
  Model?: string
  Serial?: string
  MfgDate?: string
  NameplatePhotoUrl?: string
  NameplateThumbnailUrl?: string
  ScanStatus?: MapScanStatus
  ScanConfidences?: { Name?: number; Make?: number; Model?: number; Serial?: number; MfgDate?: number }
  // Nameplate electrical ratings — no per-field confidence by design; the UI
  // warns and asks the user to validate each row manually.
  Ratings?: MapRatingITF[]
  /** Set once the pin is converted into an Equipment-library record. */
  EquipmentId?: string
  Latitude?: string
  Longitude?: string
  GeoAccuracy?: number
  GeoSource?: 'Device' | 'PhotoExif' | 'Manual'
  PhotoUrls?: string[]
  ThumbnailUrls?: string[]
  Status: MappingHubStatus
}

export interface MapBuildingITF {
  acId: string
  Name?: string
  Latitude?: string
  Longitude?: string
}

export interface MappingHubITF {
  Categories: MapCategoryITF[]
  Building: MapBuildingITF | null
  Buildings?: MapBuildingITF[]
  /** Hero photo behind the hub title on the mobile home view. */
  HeaderImageUrl?: string | null
}

export interface MapCategoriesAdminITF {
  Categories: MapCategoryITF[]
  Settings: MapCategorySettingITF[]
}
